"use client";

import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import type { Locale } from "date-fns";
import { useTranslations } from "next-intl";
import { CalendarNew } from "@/components/ui/calendar-new";
import { toDateKey } from "@/features/addons/hooks/use-diet-addon-modal";
import { DietAddonCardDetails } from "./diet-addon-card-details";
import { DietAddonDayList } from "./diet-addon-day-list";

type DietAddonEditorBodyProps = {
  addon: AddonDietAddonsRead;
  // Seller shown in the addon header when the catalog names none (MPL-3832).
  cateringName?: string;
  calendarLocale: Locale;
  allowedDates: Date[];
  allowedKeys: Set<string>;
  selectedDates: Date[];
  selection: Record<string, number>;
  canAddMore: boolean;
  isAllSelected: boolean;
  onSyncSelectedDates: (dates: Date[] | undefined) => void;
  onIncrementDay: (date: Date) => void;
  onDecrementDay: (date: Date) => void;
  onRemoveDay: (date: Date) => void;
  onToggleAll: () => void;
};

// Presentational body shared by the standalone addon modal (MPL-3724) and the
// summary dialog's swapped add/edit view (MPL-3728): addon details, the day
// picker and the per-day quantity list. All state lives in useDietAddonModal.
export const DietAddonEditorBody = ({
  addon,
  cateringName,
  calendarLocale,
  allowedDates,
  allowedKeys,
  selectedDates,
  selection,
  canAddMore,
  isAllSelected,
  onSyncSelectedDates,
  onIncrementDay,
  onDecrementDay,
  onRemoveDay,
  onToggleAll,
}: DietAddonEditorBodyProps) => {
  const t = useTranslations();

  return (
    <div className="mx-auto flex w-full max-w-215 flex-col gap-6">
      <DietAddonCardDetails addon={addon} cateringName={cateringName} />

      <div className="flex flex-col gap-4">
        <div className="flex w-full items-center justify-between gap-4">
          <h3 className="hidden flex-1 font-medium text-brand-green2-300 text-xl md:block">
            {t("cart.configuration.addons.modal.daysHeading")}
          </h3>
          <button
            className="shrink-0 font-medium text-base text-brand-green2-300 underline underline-offset-4"
            onClick={onToggleAll}
            type="button"
          >
            {isAllSelected
              ? t("cart.configuration.addons.modal.deselectAll")
              : t("cart.configuration.addons.modal.selectAll")}
          </button>
        </div>

        <div className="flex flex-col gap-4 md:flex-row md:items-stretch">
          <div className="rounded-xl bg-brand-white p-2 md:w-89 md:shrink-0">
            <CalendarNew
              defaultMonth={allowedDates.at(0)}
              disabled={(date) => !allowedKeys.has(toDateKey(date))}
              endMonth={allowedDates.at(-1)}
              locale={calendarLocale}
              mode="multiple"
              onSelect={onSyncSelectedDates}
              selected={selectedDates}
              startMonth={allowedDates.at(0)}
            />
          </div>

          {/* On md+ the day list scrolls inside the calendar's height instead
              of growing the row: its content is absolutely positioned so it
              adds no intrinsic height, and the calendar drives the row height. */}
          <div className="relative min-h-0 flex-1 md:self-stretch">
            <div className="flex md:absolute md:inset-0 md:overflow-y-auto">
              <DietAddonDayList
                canAddMore={canAddMore}
                onDecrement={onDecrementDay}
                onIncrement={onIncrementDay}
                onRemove={onRemoveDay}
                selectedDates={selectedDates}
                selection={selection}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
